// Datenzugriff des Eigentümerportals – nur über die Portal-Funktion der Datenbank (SECURITY DEFINER,
// Eigentümer aus auth.uid(), Portal-Schalter geprüft). Keine direkten Tabellenzugriffe, nichts wird versendet.
import { supabase, supabaseAnonKey, supabaseUrl } from '../supabase/client';
import type { EigDaten } from './eigentuemer';
import type { EigDokument, EigTermin, PortalRechnung, RechnungStand } from './eigentuemerE7';
import type { EinsatzDetail, EinsatzKurz, KostenStand } from './einsaetze';

export async function ladeEigentuemerDaten(): Promise<EigDaten | null> {
  const { data, error } = await supabase.rpc('portal_eigentuemer_daten');
  if (error) { console.error('Eigentümerportal: Daten laden fehlgeschlagen', error.message); return null; }
  return (data as EigDaten | null) ?? null;
}

const OBJEKT_SCHLUESSEL = 'eigentuemerportal_objekt';

/** Zuletzt gewähltes Objekt (nur Bequemlichkeit im eigenen Browser). */
export function gemerktesObjekt(): string | null {
  try { return localStorage.getItem(OBJEKT_SCHLUESSEL); } catch { return null; }
}

export function merkeObjekt(id: string): void {
  try { localStorage.setItem(OBJEKT_SCHLUESSEL, id); } catch { /* ohne Speicher geht es auch */ }
}

const AUSWAHL_SCHLUESSEL = 'eigentuemerportal_gewaehlt';

/** #584: In dieser Sitzung schon ein Objekt gewählt? (sessionStorage – nach neuem Login wieder die Vorauswahl) */
export function auswahlGetroffen(): boolean {
  try { return sessionStorage.getItem(AUSWAHL_SCHLUESSEL) === '1'; } catch { return false; }
}

export function merkeAuswahl(getroffen: boolean): void {
  try { if (getroffen) sessionStorage.setItem(AUSWAHL_SCHLUESSEL, '1'); else sessionStorage.removeItem(AUSWAHL_SCHLUESSEL); } catch { /* ohne Speicher geht es auch */ }
}

// ── E7: Rechnungen, Termine, Dokumente ──

export async function ladeMeineRechnungen(): Promise<PortalRechnung[]> {
  const { data, error } = await supabase.rpc('portal_meine_rechnungen');
  if (error) { console.error('Eigentümerportal: Rechnungen laden fehlgeschlagen', error.message); return []; }
  return (data as PortalRechnung[] | null) ?? [];
}

/** Einzige Schreibaktion: Rechnung freigeben oder ablehnen (die Datenbank prüft Berechtigung und Begründung). */
export async function entscheideRechnung(id: string, entscheidung: Exclude<RechnungStand, 'offen'>, begruendung?: string): Promise<void> {
  const { error } = await supabase.rpc('portal_rechnung_entscheiden', {
    p_id: id, p_entscheidung: entscheidung, p_begruendung: begruendung?.trim() || null,
  });
  if (error) throw new Error(error.message);
}

export async function ladeEigentuemerTermine(): Promise<EigTermin[]> {
  const { data, error } = await supabase.rpc('portal_eigentuemer_termine');
  if (error) { console.error('Eigentümerportal: Termine laden fehlgeschlagen', error.message); return []; }
  return (data as EigTermin[] | null) ?? [];
}

export async function ladeEigentuemerDokumente(): Promise<EigDokument[]> {
  const { data, error } = await supabase.rpc('portal_eigentuemer_dokumente');
  if (error) { console.error('Eigentümerportal: Dokumente laden fehlgeschlagen', error.message); return []; }
  return (data as EigDokument[] | null) ?? [];
}

// ── #564 Einsätze ──

export async function ladeEinsaetze(): Promise<EinsatzKurz[]> {
  const { data, error } = await supabase.rpc('portal_meine_einsaetze');
  if (error) { console.error('Eigentümerportal: Einsätze laden fehlgeschlagen', error.message); return []; }
  return (data as EinsatzKurz[] | null) ?? [];
}

export async function ladeEinsatzDetail(id: string): Promise<EinsatzDetail | null> {
  const { data, error } = await supabase.rpc('portal_einsatz_detail', { p_id: id });
  if (error) { console.error('Eigentümerportal: Einsatz laden fehlgeschlagen', error.message); return null; }
  return (data as EinsatzDetail | null) ?? null;
}

/** Markiertes Einsatz-Foto über portal-datei (prüft bei jedem Abruf); liefert eine lokale Bild-URL. */
export async function ladeEinsatzFoto(id: string): Promise<string> {
  const { data: sitzung } = await supabase.auth.getSession();
  const token = sitzung.session?.access_token;
  if (!token) throw new Error('Bitte melden Sie sich erneut an.');
  const res = await fetch(`${supabaseUrl}/functions/v1/portal-datei?foto=${encodeURIComponent(id)}`, {
    headers: { Authorization: `Bearer ${token}`, apikey: supabaseAnonKey },
  });
  if (!res.ok) throw new Error('Das Foto konnte nicht geladen werden.');
  return URL.createObjectURL(await res.blob());
}

// ── #564 T4 Kostenfreigabe ──
export async function ladeKostenStand(id: string): Promise<KostenStand | null> {
  const { data, error } = await supabase.rpc('portal_kosten_stand', { p_ticket: id });
  if (error) { console.error('Eigentümerportal: Kostenstand laden fehlgeschlagen', error.message); return null; }
  return (data as KostenStand | null) ?? null;
}

/** Zweite Schreibaktion: Kosten eines Einsatzes freigeben oder ablehnen (Datenbank prüft Berechtigung und Grund). */
export async function entscheideKosten(id: string, entscheidung: 'freigegeben' | 'abgelehnt', begruendung?: string): Promise<void> {
  const { error } = await supabase.rpc('portal_kosten_entscheiden', { p_ticket: id, p_entscheidung: entscheidung, p_begruendung: begruendung?.trim() || null });
  if (error) throw new Error(error.message);
}
